import {
  Badge,
  mergeClasses,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
} from '@fluentui/react-components';

import type { DatasetUsageCategory } from '../../../services/dataHubService.interface.js';

import type { UsageTableProps } from './DatasetUsageSection.interface.js';
import { useStyles } from './DatasetUsageSection.style.js';

const COLUMN_DATE = 'Date';
const COLUMN_ACTION = 'Action';
const COLUMN_CATEGORY = 'Category';
const COLUMN_APPLICATION = 'Application';
const COLUMN_COUNT = 'Count';
const COLUMN_UNIQUE_USERS = 'Unique Users';
const TABLE_ARIA_LABEL = 'Dataset usage by day';

type CategoryBadgeColor = 'success' | 'informative' | 'warning' | 'danger';

const CATEGORY_BADGE_COLOR: Record<DatasetUsageCategory, CategoryBadgeColor> = {
  Create: 'success',
  Read: 'informative',
  Update: 'warning',
  Delete: 'danger',
};

function formatDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function UsageTable({ records }: UsageTableProps) {
  const styles = useStyles();

  return (
    <Table className={styles.table} size="small" aria-label={TABLE_ARIA_LABEL}>
      <TableHeader>
        <TableRow>
          <TableHeaderCell className={styles.colDate}>
            {COLUMN_DATE}
          </TableHeaderCell>
          <TableHeaderCell className={styles.colFlex}>
            {COLUMN_ACTION}
          </TableHeaderCell>
          <TableHeaderCell className={styles.colCategory}>
            {COLUMN_CATEGORY}
          </TableHeaderCell>
          <TableHeaderCell className={styles.colFlex}>
            {COLUMN_APPLICATION}
          </TableHeaderCell>
          <TableHeaderCell
            className={mergeClasses(styles.colCount, styles.numericHeader)}
          >
            {COLUMN_COUNT}
          </TableHeaderCell>
          <TableHeaderCell
            className={mergeClasses(styles.colUnique, styles.numericHeader)}
          >
            {COLUMN_UNIQUE_USERS}
          </TableHeaderCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        {records.map((record) => (
          <TableRow
            key={`${record.date}|${record.category}|${record.action}|${record.application}`}
          >
            <TableCell className={styles.colDate}>
              {formatDate(record.date)}
            </TableCell>
            <TableCell className={styles.colFlex}>
              <Text truncate wrap={false} title={record.action}>
                {record.action}
              </Text>
            </TableCell>
            <TableCell className={styles.colCategory}>
              <Badge
                appearance="tint"
                color={CATEGORY_BADGE_COLOR[record.category]}
              >
                {record.category}
              </Badge>
            </TableCell>
            <TableCell className={styles.colFlex}>
              <Badge
                appearance="tint"
                color="brand"
                className={styles.appBadge}
                title={record.application}
              >
                {record.application}
              </Badge>
            </TableCell>
            <TableCell
              className={mergeClasses(styles.colCount, styles.numericCell)}
            >
              <Text>{record.count}</Text>
            </TableCell>
            <TableCell
              className={mergeClasses(styles.colUnique, styles.numericCell)}
            >
              <Text>{record.uniqueUsers}</Text>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
